import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { getMyLibrary } from '../api/libraryService';
import { readingProgressService } from '../api/readingProgressService';
import type { LibraryBook, LicenseWithNavigationPropertiesDto } from '../types/library';
import BooksellerPill from '../components/library/BooksellerPill';
import { useTranslation } from '../i18n/useTranslation';
import {
  BookOpen,
  Search,
  LibraryBig,
  AlertCircle,
  Loader2,
  LayoutGrid,
  List,
  Rows3,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X
} from 'lucide-react';
import { CompactOfflineButton } from '../components/offline/CompactOfflineButton';

// View modes for library display
type ViewMode = 'shelves' | 'grid' | 'list';

const licenseToLibraryBook = (row: LicenseWithNavigationPropertiesDto): LibraryBook => ({
  ...row.book,
  licenseId: row.license.id,
  licenseStatus: row.license.status,
  lastSyncedAt: row.license.lastSyncedAt ?? null,
  booksellerId: row.license.booksellerId ?? row.bookseller?.id ?? null,
  booksellerSlug: row.bookseller?.slug ?? null,
  booksellerName: row.bookseller?.name ?? null,
  booksellerLogoUrl: row.bookseller?.logoUrl ?? null,
});

const parseSyncTimestamp = (s?: string | null): number => {
  if (!s) return 0;
  const n = Date.parse(s);
  return Number.isNaN(n) ? 0 : n;
};

// Read status filter
type ReadStatus = 'all' | 'unread' | 'in-progress' | 'completed';

interface BookWithProgress extends LibraryBook {
  readingProgress?: number; // 0-100
  lastReadAt?: Date;
}

export default function MyLibraryPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [books, setBooks] = useState<BookWithProgress[]>([]);
  const [filteredBooks, setFilteredBooks] = useState<BookWithProgress[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('shelves');
  const [readStatus, setReadStatus] = useState<ReadStatus>('all');
  const [selectedGenre, setSelectedGenre] = useState<string>('all');
  const [selectedAuthor, setSelectedAuthor] = useState<string>('all');
  const [selectedPublisher, setSelectedPublisher] = useState<string>('all');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [brokenImages, setBrokenImages] = useState<Set<string>>(new Set());
  const continueReadingRef = useRef<HTMLDivElement>(null);

  const scrollContinueReading = (direction: 'left' | 'right') => {
    const container = continueReadingRef.current;
    if (!container) return;
    const scrollAmount = container.clientWidth * 0.8;
    container.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  const handleImageError = (bookId: string) => {
    setBrokenImages(prev => new Set(prev).add(bookId));
  };

  // Convert Azure blob storage URL to proxy URL
  const getProxyImageUrl = (blobUrl: string | undefined): string | undefined => {
    if (!blobUrl) return undefined;

    try {
      let relativePath: string;

      if (!blobUrl.startsWith('http://') && !blobUrl.startsWith('https://')) {
        relativePath = blobUrl.trim();
      } else {
        const url = new URL(blobUrl);
        const pathParts = url.pathname.split('/');
        relativePath = pathParts.slice(2).join('/');
      }

      if (!relativePath) return undefined;

      const apiBaseUrl = import.meta.env.VITE_API_URL || '';
      return `${apiBaseUrl}/api/app/books-extn/image?path=${encodeURIComponent(relativePath)}`;
    } catch (error) {
      console.error('Failed to parse blob URL:', blobUrl, error);
      return undefined;
    }
  };

  // Fetch user's library
  useEffect(() => {
    loadLibrary();
  }, []);

  // Apply filters
  useEffect(() => {
    let filtered = books;

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (book) =>
          book.title.toLowerCase().includes(query) ||
          book.author?.toLowerCase().includes(query) ||
          book.isbn?.toLowerCase().includes(query)
      );
    }

    // Read status filter
    if (readStatus !== 'all') {
      filtered = filtered.filter((book) => {
        const progress = book.readingProgress || 0;
        if (readStatus === 'unread') return progress === 0;
        if (readStatus === 'in-progress') return progress > 0 && progress < 100;
        if (readStatus === 'completed') return progress === 100;
        return true;
      });
    }

    // Genre filter (TODO: Add genre field to BookDto)
    // Author filter
    if (selectedAuthor !== 'all') {
      filtered = filtered.filter((book) => book.author === selectedAuthor);
    }

    // Publisher filter (TODO: Need to join with publisher data)

    setFilteredBooks(filtered);
  }, [searchQuery, books, readStatus, selectedGenre, selectedAuthor, selectedPublisher]);

  const loadLibrary = async () => {
    try {
      setLoading(true);
      setError(null);

      const [licenseRows, progressList] = await Promise.all([
        getMyLibrary(),
        readingProgressService.getAllMyProgress().catch((err) => {
          console.warn('Failed to load reading progress, falling back to no progress:', err);
          return [];
        }),
      ]);

      const progressByBookId = new Map(
        progressList.map((p) => [p.bookId, p])
      );

      // Dedupe in the rare case a user has multiple licenses for the same book; prefer most-recent sync.
      const byBookId = new Map<string, LibraryBook>();
      for (const row of licenseRows) {
        const next = licenseToLibraryBook(row);
        const existing = byBookId.get(next.id);
        if (!existing) {
          byBookId.set(next.id, next);
          continue;
        }
        const existingSync = parseSyncTimestamp(existing.lastSyncedAt);
        const nextSync = parseSyncTimestamp(next.lastSyncedAt);
        if (nextSync >= existingSync) byBookId.set(next.id, next);
      }

      const booksWithProgress: BookWithProgress[] = Array.from(byBookId.values()).map((book) => {
        const progress = progressByBookId.get(book.id);
        return {
          ...book,
          readingProgress: progress ? Math.round(Number(progress.progressPercentage)) : 0,
          lastReadAt: progress ? new Date(progress.lastReadAt) : undefined,
        };
      });

      setBooks(booksWithProgress);
      setFilteredBooks(booksWithProgress);
    } catch (err: any) {
      console.error('Failed to load library:', err);
      setError(err?.response?.data?.error?.message || 'Failed to load your library. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBookClick = (bookId: string) => {
    navigate(`/read/${bookId}`);
  };

  // Continue Reading: all in-progress books (opened but not completed), most recently read first
  const booksInProgress = filteredBooks
    .filter((book) => {
      const progress = book.readingProgress ?? 0;
      return !!book.lastReadAt && progress > 0 && progress < 100;
    })
    .sort((a, b) => {
      const aTime = a.lastReadAt ? a.lastReadAt.getTime() : 0;
      const bTime = b.lastReadAt ? b.lastReadAt.getTime() : 0;
      return bTime - aTime;
    });

  // Get unique authors and publishers for filters
  const uniqueAuthors = Array.from(new Set(books.map((b) => b.author).filter(Boolean)));
  // TODO: Get unique publishers when we have publisher data joined

  // Loading state
  if (loading) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[50vh]">
          <Loader2 className="w-12 h-12 text-primary-600 animate-spin mb-4" />
          <p className="text-neutral-600">{t('App.Library.Loading')}</p>
        </div>
      </Layout>
    );
  }

  // Error state
  if (error) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[50vh]">
          <AlertCircle className="w-16 h-16 text-error-500 mb-4" />
          <h2 className="text-2xl font-bold text-neutral-900 mb-2">{t('App.Library.ErrorLoadingTitle')}</h2>
          <p className="text-neutral-600 mb-6 text-center max-w-md">{error}</p>
          <button
            onClick={loadLibrary}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors min-h-[44px]"
          >
            {t('App.Library.TryAgain')}
          </button>
        </div>
      </Layout>
    );
  }

  // Empty state
  if (books.length === 0) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[50vh]">
          <LibraryBig className="w-20 h-20 text-neutral-400 mb-4" />
          <h2 className="text-2xl font-bold text-neutral-900 mb-2">{t('App.Library.EmptyTitle')}</h2>
          <p className="text-neutral-600 text-center max-w-md mb-6">
            {t('App.Library.EmptyDescription')}
          </p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="w-full">
        {/* Header with title and view toggles */}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <h1 className="text-3xl sm:text-4xl font-black text-neutral-900 tracking-tight">
            {t('App.Library.Title')}
          </h1>

          {/* View toggle buttons - Desktop only */}
          <div className="hidden md:flex gap-2 ml-auto">
            <button
              onClick={() => setViewMode('shelves')}
              className={`p-2 rounded-lg transition-colors min-h-[44px] min-w-[44px] ${
                viewMode === 'shelves'
                  ? 'bg-primary-100 text-primary-600'
                  : 'text-neutral-500 hover:bg-neutral-100'
              }`}
              aria-label={t('App.Library.ViewMode.Shelves')}
              aria-pressed={viewMode === 'shelves'}
            >
              <Rows3 className="w-5 h-5" aria-hidden="true" />
            </button>
            {/* Removed grid view back in favor of list view */}
            {/* <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg transition-colors min-h-[44px] min-w-[44px] ${
                viewMode === 'grid'
                  ? 'bg-primary-100 text-primary-600'
                  : 'text-neutral-500 hover:bg-neutral-100'
              }`}
              aria-label="Grid view"
              aria-pressed={viewMode === 'grid'}
            >
              <LayoutGrid className="w-5 h-5" aria-hidden="true" />
            </button> */}
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-lg transition-colors min-h-[44px] min-w-[44px] ${
                viewMode === 'list'
                  ? 'bg-primary-100 text-primary-600'
                  : 'text-neutral-500 hover:bg-neutral-100'
              }`}
              aria-label={t('App.Library.ViewMode.List')}
              aria-pressed={viewMode === 'list'}
            >
              <List className="w-5 h-5" aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Filters Sidebar */}
          <aside className="lg:w-64 flex-shrink-0">
            {/* Mobile filter toggle */}
            <button
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              className="lg:hidden w-full flex items-center justify-between px-4 py-3 bg-white border border-neutral-200 rounded-lg mb-4 min-h-[44px]"
              aria-expanded={showMobileFilters}
              aria-controls="filters-panel"
            >
              <span className="font-semibold text-neutral-900">{t('App.Library.Filters')}</span>
              <ChevronDown
                className={`w-5 h-5 text-neutral-500 transition-transform ${
                  showMobileFilters ? 'rotate-180' : ''
                }`}
                aria-hidden="true"
              />
            </button>

            {/* Filters panel */}
            <div
              id="filters-panel"
              className={`space-y-6 ${showMobileFilters ? 'block' : 'hidden lg:block'}`}
            >
              <h2 className="text-lg font-bold text-neutral-900 px-2">{t('App.Library.Filters')}</h2>

              {/* Read Status */}
              <div>
                <h3 className="px-2 mb-3 text-[10px] font-bold text-neutral-400 uppercase tracking-wider">{t('App.Library.Filters.ReadStatus')}</h3>
                <div className="space-y-2">
                  {[
                    { value: 'all' as ReadStatus, label: t('App.Library.Filters.AllBooks') },
                    { value: 'unread' as ReadStatus, label: t('App.Library.Filters.Unread') },
                    { value: 'in-progress' as ReadStatus, label: t('App.Library.Filters.InProgress') },
                    { value: 'completed' as ReadStatus, label: t('App.Library.Filters.Completed') },
                  ].map((status) => (
                    <button
                      key={status.value}
                      onClick={() => setReadStatus(status.value)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-colors min-h-[44px] ${
                        readStatus === status.value
                          ? 'bg-primary-100 text-primary-700'
                          : 'text-neutral-700 hover:bg-neutral-100'
                      }`}
                      aria-pressed={readStatus === status.value}
                    >
                      {status.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Author Filter */}
              <div>
                <label htmlFor="author-filter" className="block px-2 mb-2 text-[10px] font-bold text-neutral-400 uppercase tracking-wider">
                  {t('App.Library.Filters.Author')}
                </label>
                <select
                  id="author-filter"
                  value={selectedAuthor}
                  onChange={(e) => setSelectedAuthor(e.target.value)}
                  className="w-full px-3 py-2 bg-neutral-100 border-none rounded-lg text-neutral-900 focus:ring-2 focus:ring-primary-500 focus:outline-none min-h-[44px]"
                >
                  <option value="all">{t('App.Library.Filters.AllAuthors')}</option>
                  {uniqueAuthors.map((author) => (
                    <option key={author} value={author}>
                      {author}
                    </option>
                  ))}
                </select>
              </div>

              {/* Genre Filter - TODO: Add when genre field is available */}
              <div>
                <label htmlFor="genre-filter" className="block px-2 mb-2 text-[10px] font-bold text-neutral-400 uppercase tracking-wider">
                  {t('App.Library.Filters.Genre')}
                </label>
                <select
                  id="genre-filter"
                  value={selectedGenre}
                  onChange={(e) => setSelectedGenre(e.target.value)}
                  className="w-full px-3 py-2 bg-neutral-100 border-none rounded-lg text-neutral-900 focus:ring-2 focus:ring-primary-500 focus:outline-none min-h-[44px]"
                  disabled
                >
                  <option value="all">{t('App.Library.Filters.AllGenres')}</option>
                </select>
              </div>

              {/* Publisher Filter - TODO: Join with publisher data */}
              <div>
                <label htmlFor="publisher-filter" className="block px-2 mb-2 text-[10px] font-bold text-neutral-400 uppercase tracking-wider">
                  {t('App.Library.Filters.Publisher')}
                </label>
                <select
                  id="publisher-filter"
                  value={selectedPublisher}
                  onChange={(e) => setSelectedPublisher(e.target.value)}
                  className="w-full px-3 py-2 bg-neutral-100 border-none rounded-lg text-neutral-900 focus:ring-2 focus:ring-primary-500 focus:outline-none min-h-[44px]"
                  disabled
                >
                  <option value="all">{t('App.Library.Filters.AllPublishers')}</option>
                </select>
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Search Bar */}
            <div className="mb-6">
              <div className="relative">
                <Search
                  className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-primary-400 pointer-events-none"
                  aria-hidden="true"
                />
                <input
                  type="text"
                  placeholder={t('App.Library.SearchPlaceholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-10 py-3 bg-primary-50/50 border border-primary-200/50 rounded-lg text-neutral-900 placeholder:text-primary-400/70 focus:ring-2 focus:ring-secondary-400 focus:border-secondary-400 focus:bg-white focus:outline-none min-h-[44px] transition-all"
                  aria-label={t('App.Library.SearchAriaLabel')}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 hover:bg-secondary-100 rounded transition-colors"
                    aria-label={t('App.Library.ClearSearch')}
                  >
                    <X className="w-4 h-4 text-primary-600" aria-hidden="true" />
                  </button>
                )}
              </div>
            </div>

            {/* Continue Reading Section - Horizontal Slider */}
            {booksInProgress.length > 0 && (
              <section className="mb-12">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl sm:text-3xl font-bold text-neutral-900">
                    {t('App.Library.ContinueReading')}
                  </h2>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-neutral-500">
                      {t('App.Library.BookCount', { count: booksInProgress.length })}
                    </span>
                    <div className="hidden sm:flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => scrollContinueReading('left')}
                        className="p-2 rounded-full border border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                        aria-label={t('App.Library.ScrollLeft')}
                      >
                        <ChevronLeft className="w-5 h-5" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => scrollContinueReading('right')}
                        className="p-2 rounded-full border border-neutral-200 bg-white text-neutral-600 hover:bg-neutral-50 hover:text-neutral-900 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                        aria-label={t('App.Library.ScrollRight')}
                      >
                        <ChevronRight className="w-5 h-5" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                </div>
                <div
                  ref={continueReadingRef}
                  className="flex gap-6 overflow-x-auto snap-x snap-mandatory scroll-smooth pb-2 -mx-1 px-1 scrollbar-hide"
                  role="list"
                  aria-label={t('App.Library.ContinueReadingAriaLabel')}
                >
                  {booksInProgress.map((book) => (
                    <article
                      key={book.id}
                      onClick={() => handleBookClick(book.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleBookClick(book.id);
                        }
                      }}
                      tabIndex={0}
                      role="listitem"
                      aria-label={t('App.Library.ContinueReadingItemAriaLabel', { title: book.title, progress: book.readingProgress })}
                      className="group relative flex-shrink-0 snap-start w-[85%] sm:w-[calc((100%-1.5rem)/2)] lg:w-[calc((100%-3rem)/3)] bg-white rounded-2xl p-5 border border-neutral-200 hover:border-neutral-300 transition-all duration-300 hover:shadow-xl cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                    >
                      <div className="flex gap-4">
                        {/* Book Cover - Compact */}
                        <div className="flex-shrink-0 w-24 h-32 bg-neutral-100 rounded-lg overflow-hidden shadow-md">
                          {(() => {
                            const proxyUrl = getProxyImageUrl(book.coverImageUrl);
                            return proxyUrl && !brokenImages.has(book.id) ? (
                              <img
                                src={proxyUrl}
                                alt={t('App.Library.CoverAltText', { title: book.title })}
                                className="w-full h-full object-cover"
                                onError={() => handleImageError(book.id)}
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <BookOpen className="w-8 h-8 text-neutral-400" aria-hidden="true" />
                              </div>
                            );
                          })()}
                        </div>

                        {/* Book Info */}
                        <div className="flex-1 min-w-0 flex flex-col">
                          <h3 className="font-semibold text-neutral-900 text-base line-clamp-2 mb-1">
                            {book.title}
                          </h3>
                          {book.author && (
                            <p className="text-sm text-neutral-600 mb-3">{book.author}</p>
                          )}
                          <BooksellerPill
                            booksellerId={book.booksellerId}
                            booksellerSlug={book.booksellerSlug}
                            booksellerName={book.booksellerName}
                            logoUrl={book.booksellerLogoUrl}
                            size="sm"
                            className="mb-2"
                          />

                          {/* Progress Bar */}
                          <div className="mt-auto">
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-medium text-neutral-700">{t('App.Library.ProgressPercent', { percent: book.readingProgress })}</span>
                            </div>
                            <div className="w-full bg-neutral-200 rounded-full h-2 overflow-hidden">
                              <div
                                className="bg-gradient-to-r from-primary-500 to-secondary-500 h-full rounded-full transition-all duration-500"
                                style={{ width: `${book.readingProgress}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </section>
            )}

            {/* All Books Section - Modern Grid Design */}
            <section className="w-full">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl sm:text-3xl font-bold text-neutral-900">
                  {t('App.Library.AllBooksSection')}
                </h2>
                <span className="text-sm text-neutral-500">{t('App.Library.BookCount', { count: filteredBooks.length })}</span>
              </div>

              {filteredBooks.length === 0 ? (
                <div className="bg-white rounded-2xl p-12 border border-neutral-200 text-center">
                  <Search className="w-16 h-16 text-neutral-300 mx-auto mb-4" aria-hidden="true" />
                  <h3 className="text-lg font-semibold text-neutral-700 mb-2">{t('App.Library.NoResultsTitle')}</h3>
                  <p className="text-neutral-500">{t('App.Library.NoResultsDescription')}</p>
                </div>
              ) : viewMode === 'shelves' ? (
                /* Modern Card Grid - Clean & Minimal */
                <div className="w-full grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-5">
                  {filteredBooks.map((book) => {
                    return (
                      <article
                        key={book.id}
                        onClick={() => handleBookClick(book.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            handleBookClick(book.id);
                          }
                        }}
                        tabIndex={0}
                        role="button"
                        aria-label={t('App.Library.OpenBookAriaLabel', { title: book.title, author: book.author || t('App.Library.UnknownAuthor') })}
                        className="group cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-xl"
                      >
                        {/* Book Cover */}
                        <div className="relative mb-3">
                          <div className="w-full aspect-[2/3] bg-gradient-to-br from-neutral-100 to-neutral-200 rounded-xl overflow-hidden shadow-md transition-all duration-300 group-hover:shadow-2xl group-hover:-translate-y-2 group-hover:scale-105">
                            {(() => {
                              const proxyUrl = getProxyImageUrl(book.coverImageUrl);
                              return proxyUrl && !brokenImages.has(book.id) ? (
                                <img
                                  src={proxyUrl}
                                  alt={t('App.Library.CoverAltText', { title: book.title })}
                                  className="w-full h-full object-cover"
                                  onError={() => handleImageError(book.id)}
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <BookOpen className="w-10 h-10 text-neutral-400" aria-hidden="true" />
                                </div>
                              );
                            })()}
                          </div>
                          <BooksellerPill
                            booksellerId={book.booksellerId}
                            booksellerSlug={book.booksellerSlug}
                            booksellerName={book.booksellerName}
                            logoUrl={book.booksellerLogoUrl}
                            size="sm"
                            className="absolute top-2 right-2 z-10"
                          />
                        </div>

                        {/* Book Info */}
                        <div className="space-y-1">
                          <h3 className="font-semibold text-neutral-900 text-sm line-clamp-2 leading-tight">
                            {book.title}
                          </h3>
                          {book.author && (
                            <p className="text-xs text-neutral-600 line-clamp-1">{book.author}</p>
                          )}
                        </div>
                      </article>
                    );
                  })}
                </div>
              ) : viewMode === 'grid' ? (
                /* Grid View - Ultra-compact with maximum density */
                <div className="w-full grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-3">
                  {filteredBooks.map((book) => (
                    <article
                      key={book.id}
                      onClick={() => handleBookClick(book.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleBookClick(book.id);
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      aria-label={t('App.Library.OpenBookAriaLabel', { title: book.title, author: book.author || t('App.Library.UnknownAuthor') })}
                      className="group cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 rounded"
                    >
                      {/* Book Cover - Ultra-compact */}
                      <div className="relative w-full aspect-[2/3] bg-gradient-to-br from-primary-50 to-secondary-50 rounded shadow-sm overflow-hidden transition-all duration-200 group-hover:shadow-md group-hover:scale-105 mb-1.5">
                        {(() => {
                          const proxyUrl = getProxyImageUrl(book.coverImageUrl);
                          return proxyUrl && !brokenImages.has(book.id) ? (
                            <img
                              src={proxyUrl}
                              alt={t('App.Library.CoverAltText', { title: book.title })}
                              className="w-full h-full object-cover"
                              onError={() => handleImageError(book.id)}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <BookOpen className="w-8 h-8 text-primary-400" aria-hidden="true" />
                            </div>
                          );
                        })()}
                        <BooksellerPill
                          booksellerId={book.booksellerId}
                          booksellerSlug={book.booksellerSlug}
                          booksellerName={book.booksellerName}
                          logoUrl={book.booksellerLogoUrl}
                          size="xs"
                          className="absolute top-1 right-1"
                        />
                      </div>

                      {/* Book Info - Ultra-compact, always visible */}
                      <div className="px-0.5">
                        <h3 className="font-medium text-neutral-900 text-[10px] leading-tight line-clamp-2 mb-0.5">
                          {book.title}
                        </h3>
                        {book.author && (
                          <p className="text-[9px] text-neutral-500 line-clamp-1 leading-tight">{book.author}</p>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              ) : (
                /* List View */
                <div className="w-full space-y-3">
                  {filteredBooks.map((book) => (
                    <article
                      key={book.id}
                      onClick={() => handleBookClick(book.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          handleBookClick(book.id);
                        }
                      }}
                      tabIndex={0}
                      role="button"
                      aria-label={t('App.Library.OpenBookAriaLabel', { title: book.title, author: book.author || t('App.Library.UnknownAuthor') })}
                      className="w-full group flex gap-4 p-4 bg-white border border-neutral-200 rounded-lg hover:border-primary-400 hover:shadow-md transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                    >
                      {/* Book Cover - Smaller in list view */}
                      <div className="flex-shrink-0 w-20 aspect-[2/3] bg-gradient-to-br from-primary-50 to-secondary-50 rounded-md overflow-hidden">
                        {(() => {
                          const proxyUrl = getProxyImageUrl(book.coverImageUrl);
                          return proxyUrl && !brokenImages.has(book.id) ? (
                            <img
                              src={proxyUrl}
                              alt={t('App.Library.CoverAltText', { title: book.title })}
                              className="w-full h-full object-cover"
                              onError={() => handleImageError(book.id)}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <BookOpen className="w-8 h-8 text-primary-400" aria-hidden="true" />
                            </div>
                          );
                        })()}
                      </div>

                      {/* Book Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-neutral-900 mb-1 line-clamp-1">
                          {book.title}
                        </h3>
                        {book.author && (
                          <p className="text-sm text-neutral-600 mb-2">{book.author}</p>
                        )}
                        <BooksellerPill
                          booksellerId={book.booksellerId}
                          booksellerSlug={book.booksellerSlug}
                          booksellerName={book.booksellerName}
                          logoUrl={book.booksellerLogoUrl}
                          size="sm"
                          className="mt-1"
                        />
                        {book.readingProgress !== undefined && book.readingProgress > 0 && (
                          <div className="mt-2">
                            <div className="flex items-center gap-2 mb-1">
                              <div className="flex-1 h-2 bg-neutral-200 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-primary-600 rounded-full transition-all"
                                  style={{ width: `${book.readingProgress}%` }}
                                  role="progressbar"
                                  aria-valuenow={book.readingProgress}
                                  aria-valuemin={0}
                                  aria-valuemax={100}
                                />
                              </div>
                              <span className="text-xs text-neutral-500 font-medium min-w-[3rem] text-right">
                                {book.readingProgress}%
                              </span>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Action Button */}
                      <div className="flex items-center">
                        <CompactOfflineButton
                          bookId={book.id}
                          variant="icon"
                          size="sm"
                        />
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>
          </div>
        </div>
      </div>

      {/* Custom scrollbar hiding - add to global CSS */}
      <style>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </Layout>
  );
}
